import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";

export interface PublicBrandingData {
  id: string;
  name: string;
  slug: string;
  branding_config: {
    theme?: {
      sidebar_bg?: string;
      sidebar_text?: string;
      sidebar_active_bg?: string;
      sidebar_active_indicator?: string;
      accent_primary?: string;
      accent_secondary?: string;
      brand_primary?: string;
    };
    logos?: {
      sidebar?: string;
      sidebar_client?: string;
      header?: string;
      favicon?: string;
    };
    meta?: {
      app_title?: string;
      support_email?: string;
    };
  };
}

/**
 * Fetch public branding for an organization by slug.
 *
 * Used on the branded login page before authentication.
 * Only enabled when a slug is provided.
 */
export function usePublicBranding(slug: string | undefined) {
  return useQuery<PublicBrandingData>({
    queryKey: ["public-branding", slug],
    queryFn: async () => {
      const { data: envelope } = await api.get<
        { status: string; data: PublicBrandingData } | PublicBrandingData
      >(`/organizations/public-branding/${slug}/`);

      // Handle envelope response
      if (
        "data" in envelope &&
        "status" in (envelope as Record<string, unknown>)
      ) {
        return (envelope as { data: PublicBrandingData }).data;
      }
      return envelope as PublicBrandingData;
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000, // 5 min — matches backend cache
    retry: false, // Don't retry on 404
  });
}
